import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import PublisherListFilters from '../../components/admin/publishers/PublisherListFilters';
import PublisherAdvancedFilters from '../../components/admin/publishers/PublisherAdvancedFilters';
import PublisherSavedViews from '../../components/admin/publishers/PublisherSavedViews';
import PublisherListTable from '../../components/admin/publishers/PublisherListTable';
import PublisherBulkActionsBar from '../../components/admin/publishers/PublisherBulkActionsBar';
import PublisherStatusChangeDialog from '../../components/admin/publishers/PublisherStatusChangeDialog';
import PublisherQuotaEditDialog from '../../components/admin/publishers/PublisherQuotaEditDialog';
import PublisherDeleteConfirmDialog from '../../components/admin/publishers/PublisherDeleteConfirmDialog';
import PublisherEditDrawer from '../../components/admin/publishers/PublisherEditDrawer';
import PublisherCreateDrawer from '../../components/admin/publishers/PublisherCreateDrawer';
import { usePublisherListQuery } from '../../hooks/usePublisherListQuery';
import { publishersManageService } from '../../api/publishersManageService';
import {
  DEFAULT_PUBLISHER_FILTER_STATE,
  type PublisherFilterState,
  type PublisherManageDto,
} from '../../types/managePublishers';
import { useTranslation } from '../../i18n/useTranslation';

export default function ManagePublishersPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();

  const filters = useMemo<PublisherFilterState>(() => {
    const f: PublisherFilterState = { ...DEFAULT_PUBLISHER_FILTER_STATE };
    const page = Number(params.get('page')); if (page > 0) f.page = page;
    const pageSize = Number(params.get('pageSize')); if (pageSize > 0) f.pageSize = pageSize;
    const q = params.get('q'); if (q) f.q = q;
    const isActive = params.get('isActive');
    if (isActive === 'true') f.isActive = true;
    if (isActive === 'false') f.isActive = false;
    const drm = params.get('drm');
    if (drm === 'true') f.isDrmEnabled = true;
    if (drm === 'false') f.isDrmEnabled = false;
    if (params.get('includeDeleted') === 'true') f.includeDeleted = true;
    const sort = params.get('sort');
    if (sort === 'name' || sort === 'creationTime' || sort === 'bookCount') f.sort = sort;
    const dir = params.get('dir');
    if (dir === 'asc' || dir === 'desc') f.dir = dir;
    const ca = params.get('createdAfter'); if (ca) f.createdAfter = ca;
    const cb = params.get('createdBefore'); if (cb) f.createdBefore = cb;
    const bcMin = Number(params.get('bookCountMin')); if (Number.isFinite(bcMin) && params.has('bookCountMin')) f.bookCountMin = bcMin;
    const bcMax = Number(params.get('bookCountMax')); if (Number.isFinite(bcMax) && params.has('bookCountMax')) f.bookCountMax = bcMax;
    return f;
  }, [params]);

  const [selection, setSelection] = useState<Map<string, PublisherManageDto>>(new Map());
  const [editId, setEditId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [dialog, setDialog] = useState<null | 'activate' | 'deactivate' | 'quotas' | 'delete'>(null);

  // Write the entire filter state to the URL. Keys not on `next` are removed,
  // so a saved view fully replaces the current filter set.
  const writeFiltersToUrl = (next: PublisherFilterState) => {
    const out = new URLSearchParams();
    const set = (k: string, v: unknown) => {
      if (v === undefined || v === null || v === '' || v === false) return;
      out.set(k, String(v));
    };
    set('page', next.page);
    set('pageSize', next.pageSize);
    set('q', next.q);
    set('isActive', next.isActive);
    set('drm', next.isDrmEnabled);
    set('includeDeleted', next.includeDeleted);
    set('sort', next.sort);
    set('dir', next.dir);
    set('createdAfter', next.createdAfter);
    set('createdBefore', next.createdBefore);
    set('bookCountMin', next.bookCountMin);
    set('bookCountMax', next.bookCountMax);
    setParams(out);
  };

  // True for any change other than pagination — i.e., anything that changes
  // the result set the user is viewing. Used to confirm before clearing selection.
  const isMeaningfulFilterChange = (a: PublisherFilterState, b: PublisherFilterState): boolean => {
    const keys: (keyof PublisherFilterState)[] = [
      'q', 'isActive', 'isDrmEnabled', 'includeDeleted',
      'sort', 'dir', 'pageSize',
      'createdAfter', 'createdBefore', 'bookCountMin', 'bookCountMax',
    ];
    return keys.some(k => a[k] !== b[k]);
  };

  const applyFilters = (next: PublisherFilterState) => {
    if (isMeaningfulFilterChange(filters, next) && selection.size > 0) {
      const ok = window.confirm(
        t('App.Admin.Publishers.ManagePublishersPage.FilterClearConfirm', { count: selection.size })
      );
      if (!ok) return;
      setSelection(new Map());
    }
    writeFiltersToUrl(next);
  };

  // Convenience for child components that want to bump a few fields.
  // Child onChange handlers pass partial patches; we merge with current filters
  // and route through applyFilters so the selection-confirm path runs uniformly.
  const updateFilters = (patch: Partial<PublisherFilterState>) => {
    applyFilters({ ...filters, ...patch });
  };

  const toggleRow = (row: PublisherManageDto) => {
    const next = new Map(selection);
    if (next.has(row.id)) next.delete(row.id); else next.set(row.id, row);
    setSelection(next);
  };

  const togglePage = (visibleRows: PublisherManageDto[], all: boolean) => {
    const next = new Map(selection);
    if (all) visibleRows.forEach(r => next.set(r.id, r));
    else visibleRows.forEach(r => next.delete(r.id));
    setSelection(next);
  };

  const { data, isLoading, error } = usePublisherListQuery(filters);

  const isPageFullySelected = (data?.items ?? []).length > 0
    && (data?.items ?? []).every(r => selection.has(r.id));

  const selectAllMatchingFilter = async () => {
    const ids = await publishersManageService.idsForFilter(filters);
    if (ids.length > 500) {
      if (!window.confirm(t('App.Admin.Publishers.ManagePublishersPage.SelectAllLargeConfirm', { count: ids.length }))) return;
    }
    const next = new Map(selection);
    ids.forEach(id => { if (!next.has(id)) next.set(id, { id } as PublisherManageDto); });
    setSelection(next);
  };

  const exportNow = async () => {
    const blob = await publishersManageService.exportExcel(filters);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `publishers-${Date.now()}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold">{t('App.Admin.Publishers.ManagePublishersPage.Title')}</h1>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="px-3 py-1.5 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
          >
            {t('App.Admin.Publishers.ManagePublishersPage.AddPublisher')}
          </button>
        </div>
        <PublisherSavedViews filters={filters} onApply={applyFilters} />
        <PublisherListFilters filters={filters} onChange={updateFilters} />
        <PublisherAdvancedFilters filters={filters} onChange={updateFilters} />
        {isLoading && <p>{t('App.Common.Loading')}</p>}
        {error && <p className="text-red-600">Failed to load publishers: {String(error)}</p>}
        {data && (
          <>
            <PublisherBulkActionsBar
              selectionCount={selection.size}
              totalCount={data.totalCount}
              visibleRows={data.items}
              isPageFullySelected={isPageFullySelected}
              filters={filters}
              onOpenDialog={(kind) => kind === 'export' ? exportNow() : setDialog(kind)}
              onSelectAllMatchingFilter={selectAllMatchingFilter}
              onClearSelection={() => setSelection(new Map())}
            />
            <PublisherListTable
              rows={data.items}
              totalCount={data.totalCount}
              filters={filters}
              onChange={updateFilters}
              selection={selection}
              onToggleRow={toggleRow}
              onTogglePage={togglePage}
              onEdit={setEditId}
            />
          </>
        )}
      </div>

      {(dialog === 'activate' || dialog === 'deactivate') && (
        <PublisherStatusChangeDialog
          open={true}
          publisherIds={Array.from(selection.keys())}
          isActive={dialog === 'activate'}
          onClose={() => setDialog(null)}
          onCompleted={(r) => window.alert(`Updated ${r.successCount} publishers; ${r.failureCount} failed.`)}
        />
      )}

      {dialog === 'quotas' && (
        <PublisherQuotaEditDialog
          open={true}
          publisherIds={Array.from(selection.keys())}
          onClose={() => setDialog(null)}
          onCompleted={(r) => window.alert(`Updated ${r.successCount} publishers; ${r.failureCount} failed.`)}
        />
      )}

      {dialog === 'delete' && (
        <PublisherDeleteConfirmDialog
          open={true}
          publisherIds={Array.from(selection.keys())}
          onClose={() => setDialog(null)}
          onCompleted={(r) => {
            window.alert(`Deleted ${r.successCount} publishers; ${r.failureCount} failed.`);
            setSelection(new Map());
          }}
        />
      )}

      <PublisherEditDrawer
        publisherId={editId}
        onClose={() => setEditId(null)}
      />

      <PublisherCreateDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Layout>
  );
}
